import { SFIcon } from "@/components/ui/sf-icon"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import type { ToasterToast } from "@/hooks/use-toast"
import { useToast } from "@/hooks/use-toast"
import { sfIcons } from "@/lib/sf-icons"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@packages/config/theme"
import { AnimatePresence, motion } from "framer-motion"
import * as React from "react"

const MAX_DESCRIPTION_LENGTH = 320

const truncateContent = (value: string) =>
  value.length > MAX_DESCRIPTION_LENGTH ? `${value.slice(0, MAX_DESCRIPTION_LENGTH)}…` : value

const SAFE_OBJECT_FIELD_LABELS: Record<string, string | null> = {
  title: null,
  message: null,
  code: "Code",
  hint: "Hint",
  details: "Details",
  reason: "Reason",
  status: "Status",
}

const extractSafeObjectContent = (value: Record<string, unknown>) => {
  const segments: string[] = []

  for (const [key, label] of Object.entries(SAFE_OBJECT_FIELD_LABELS)) {
    const maybeValue = value[key]
    if (typeof maybeValue !== "string") {
      continue
    }
    const trimmed = maybeValue.trim()
    if (!trimmed) {
      continue
    }
    segments.push(label ? `${label}: ${trimmed}` : trimmed)
  }

  if (segments.length > 0) {
    return truncateContent(segments.join(" • "))
  }

  const fallbackString = Object.values(value).find(
    (candidate): candidate is string => typeof candidate === "string" && candidate.trim().length > 0
  )

  if (fallbackString) {
    return truncateContent(fallbackString.trim())
  }

  return undefined
}

const toRenderableContent = (
  value: React.ReactNode | undefined,
  fallback?: React.ReactNode
): React.ReactNode | undefined => {
  if (value === null || value === undefined) {
    return fallback
  }

  if (React.isValidElement(value)) {
    return value
  }

  if (value instanceof Error) {
    const message = value.message?.trim()
    return message?.length ? truncateContent(message) : fallback
  }

  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed.length > 0 ? truncateContent(trimmed) : fallback
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return truncateContent(String(value))
  }

  if (typeof value === "object") {
    const safeContent = extractSafeObjectContent(value as Record<string, unknown>)
    if (safeContent) {
      return safeContent
    }
  }

  return fallback
}

export function Toaster() {
  const { toasts, dismiss } = useToast()
  const [expandedStacks, setExpandedStacks] = React.useState<Set<string>>(new Set())

  const renderToast = React.useCallback((toast: ToasterToast) => {
    const isError = toast.variant === "destructive" || toast.variant === "error"

    const normalizedTitle = toRenderableContent(
      toast.title,
      isError ? "Error" : "Notification"
    )

    const normalizedDescription = toRenderableContent(
      toast.description,
      isError ? "An error occurred" : undefined
    )

    return (
      <Toast key={toast.id} {...toast} duration={toast.duration}>
        <div className="grid gap-1 flex-1 min-w-0">
          {normalizedTitle && <ToastTitle>{normalizedTitle}</ToastTitle>}
          {normalizedDescription && (
            <ToastDescription>{normalizedDescription}</ToastDescription>
          )}
        </div>
        {toast.action && <div className="flex-shrink-0">{toast.action}</div>}
        <ToastClose />
      </Toast>
    )
  }, [])

  // Group toasts for stacking (iOS-style)
  // When multiple toasts exist, show first one + collapse button
  const shouldStack = toasts.length > 1
  const visibleToasts = shouldStack ? toasts.slice(0, 1) : toasts
  const hiddenToasts = shouldStack ? toasts.slice(1) : []
  const stackId = shouldStack ? `stack-${toasts[0]?.id}` : null
  const isStackExpanded = stackId ? expandedStacks.has(stackId) : false

  return (
    <ToastProvider
      duration={semanticTokens.toast.duration}
      swipeDirection={semanticTokens.toast.swipeDirection}
    >
      {/* Visible toasts */}
      {visibleToasts.map(renderToast)}

      {/* Stack indicator (iOS-style) */}
      {shouldStack && hiddenToasts.length > 0 && (
        <motion.button
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          onClick={() => {
            if (stackId) {
              setExpandedStacks(prev => {
                const next = new Set(prev)
                if (isStackExpanded) {
                  next.delete(stackId)
                } else {
                  next.add(stackId)
                }
                return next
              })
            }
          }}
          className={cn(
            "relative flex items-center justify-center gap-2",
            "w-full max-w-[400px] min-w-[320px]",
            "h-12 px-4 rounded-2xl",
            "bg-black/5 dark:bg-white/5",
            "backdrop-blur-sm",
            "border border-white/10",
            semanticTokens.fg.muted,
            "text-sm font-medium",
            "touch-manipulation",
            "active:bg-black/10 dark:active:bg-white/10",
            semanticTokens.motion.fast,
            "mt-2", // Gap from visible toast
          )}
          aria-label={isStackExpanded ? "Collapse notifications" : `Expand ${hiddenToasts.length} more notification${hiddenToasts.length > 1 ? 's' : ''}`}
        >
          <span>{hiddenToasts.length} more</span>
          <SFIcon
            icon={isStackExpanded ? sfIcons.chevronUp : sfIcons.chevronDown}
            className="h-4 w-4"
          />
        </motion.button>
      )}

      {/* Expanded stack toasts */}
      {shouldStack && isStackExpanded && (
        <AnimatePresence>
          {hiddenToasts.map((toast, index) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{
                duration: 0.24, // canonical 240ms
                ease: [0.25, 1, 0.5, 1], // ease-standard
                delay: index * 0.05, // Stagger animation
              }}
              className="mt-2"
            >
              {renderToast(toast)}
            </motion.div>
          ))}
        </AnimatePresence>
      )}

      <ToastViewport />
    </ToastProvider>
  )
}
